import { createElement } from 'react';
import { createRoot } from 'react-dom/client';

import { sanitizePaidPlan } from 'features/managePlan/FocusView/helper';
import PaywallsRootModal from 'features/productGrowth/Paywalls/PaywallsRootModal';

import { toI18n } from 'util/i18n';

// Global reference to track if paywall is already shown
let isPaywallShown = false;
let paywallRoot = null;

const cleanupPaywall = () => {
  if (paywallRoot) {
    paywallRoot.unmount();
    paywallRoot = null;
  }

  const container = document.getElementById('paywall-modal-container');

  if (container && container.parentNode) {
    container.parentNode.removeChild(container);
  }

  isPaywallShown = false;
};

/**
 * @typedef {Object} ShowUpgradeModalParams
 * @property {string} minTier - Tier name like 'plus', 'pro', etc.
 * @property {string} feature - Feature name for upgrade tracking
 * @property {string} [locationProperty] - Optional location property
 * @property {string} [serverKey] - Optional server key for tracking
 * @property {Object} [mppFocusViewProps] - Focus view properties
 * @property {string} [mppFocusViewProps.focusedFeature] - Feature to focus on
 */

/**
 * Shows an upgrade modal using the new PaywallsRootModal
 * @param {ShowUpgradeModalParams} params
 */
export const showUpgradeModal = ({
  minTier,
  feature,
  locationProperty,
  serverKey,
  mppFocusViewProps,
}) => {
  // Prevent multiple paywalls from showing
  if (isPaywallShown) {
    return;
  }

  const tier = sanitizePaidPlan(minTier);

  // Create a container for the paywall
  const container = document.createElement('div');
  container.id = 'paywall-modal-container';
  document.body.appendChild(container);

  isPaywallShown = true;

  // Get the Redux store from the Rails bridge (following existing pattern)
  // This matches how other utilities access the store, e.g., EnforcementTooltipWrapperBackbone
  const store = window.Homebase?.RailsReactBridge?.store;

  if (!store) {
    console.error(
      'PaywallsRootModal: Redux store not found. Make sure the app is initialized.'
    );
    return;
  }

  // Create root and render with theme providers
  // Following the pattern used by EnforcementTooltipWrapperBackbone and DemoAccountWrapper:
  // components receive store as prop and wrap children with Redux Provider
  paywallRoot = createRoot(container);
  paywallRoot.render(
    createElement(PaywallsRootModal, {
      tier,
      feature: feature || toI18n('paywalls.features.default'),
      focusedFeature: mppFocusViewProps?.focusedFeature,
      onClose: cleanupPaywall,
      serverKey,
      locationProperty,
      store,
    })
  );
};
